import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import SessionClipsPanel from './SessionClipsPanel';
import { fetchSessionReels } from '../../services/training';
import { formatTimestamp } from '../../utils/videoTime';

/**
 * Clips tab content: editors get SessionClipsPanel; members get a read-only list.
 */
export default function SessionClipsTab({ session, videoRef, canEdit = false }) {
  if (canEdit) {
    return (
      <SessionClipsPanel
        session={session}
        canEdit
        videoRef={videoRef}
        embedded
      />
    );
  }

  return <SessionClipsReadOnly session={session} videoRef={videoRef} />;
}

function SessionClipsReadOnly({ session, videoRef }) {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!session?.id) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    fetchSessionReels(session.id)
      .then((list) => {
        if (active) setReels(list);
      })
      .catch((err) => {
        if (active) setError(err.message || 'No se pudieron cargar los clips');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [session?.id]);

  const seekTo = (seconds) => {
    const video = videoRef?.current;
    if (!video || seconds == null) return;
    video.currentTime = seconds;
    video.play().catch(() => {});
  };

  if (loading) {
    return (
      <p className="text-sm text-[var(--text-secondary)] font-bold uppercase tracking-widest">
        Cargando clips…
      </p>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
        {error}
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="max-w-2xl">
        <h3 className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] mb-4">
          Clips de esta sesión
        </h3>
        <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
          Aún no hay clips publicados para esta sesión.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <h3 className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] mb-4">
        Clips de esta sesión
      </h3>
      <ul className="grid gap-4">
        {reels.map((reel) => (
          <li key={reel.id}>
            <button
              type="button"
              onClick={() => seekTo(reel.clipStartSeconds)}
              className="w-full flex items-center gap-4 p-5 bg-[var(--bg-surface)] rounded-2xl border border-[var(--border-subtle)] shadow-sm text-left group hover:border-brand-green/30 transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <SafeIcon name="Play" className="w-4 h-4 text-brand-green" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[var(--text-main)] font-bold text-sm leading-snug truncate">
                  {reel.caption || session.title}
                </p>
                <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mt-1">
                  {formatTimestamp(reel.clipStartSeconds ?? 0)}
                  {' — '}
                  {formatTimestamp(reel.clipEndSeconds ?? 0)}
                </p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
